"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  ArrowUpRight,
  CalendarSync,
  ChevronLeft,
  ChevronRight,
  CircleStop,
  Plus,
  RotateCcw,
} from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  addDaysISO,
  getStudyPlan,
  STUDY_PLAN_IDS,
  STUDY_PLAN_LABELS,
  type StudyPlanId,
} from "@/lib/study-plan";
import {
  REPLAN_MODELS,
  taskSortValue,
  type ReplanModel,
  type ScheduledTask,
} from "@/lib/study-plan-state";
import type { ScheduleData } from "./useScheduleData";
import TaskCard, { type MoveTarget, type TaskCardActions } from "./TaskCard";
import WeekBoard from "./WeekBoard";
import {
  computeDropOrder,
  DragClone,
  dropTargetFrozen,
  freezeColumns,
  isUnchangedColumnOrder,
  type DropIndicator,
  type FrozenColumns,
} from "./schedule-dnd";
import MonthGrid from "./MonthGrid";
import DayDetailDialog from "./DayDetailDialog";
import StartPlanCTA from "./StartPlanCTA";
import { todayKstISO } from "@/lib/kst-date";
import type { StartTiming } from "@/hooks/usePlanDynamicState";
import {
  PLAN_LEGEND_DOT,
  PLAN_START_PILL,
  WINDOW_DAYS,
  clampISO,
  dateFromISO,
  formatMonthDay,
  formatYearMonth,
} from "./schedule-shared";

type ViewMode = "week" | "month";
type PlanFilter = StudyPlanId | "all";
/**
 * 구독 직후 "오늘 기준으로 다시 분배할까요?" 안내(JCG-3652). 구독 시작일이 계획 페이지의 정규
 * 시작일로 고정되므로 오늘이 그날이 아니면 캘린더가 비어 있거나(early) 앞부분이 밀려 있다(late).
 * 값은 subscribe 결과(SubscribeResult.timing)에서 오며 여기서 다시 판정하지 않는다.
 */
type StartPrompt = {
  timing: Exclude<StartTiming, "onTime">;
  startDateISO: string;
};

/** "오늘 기준 다시 분배" 다이얼로그가 열린 이유. 제목·안내 줄·취소 문구가 이 한 값으로 갈린다. */
type ReplanDialogState =
  | { reason: "manual" }
  | ({ reason: "start" } & StartPrompt)
  | null;

/** 구독 직후 안내의 제목·amber 안내 줄. 재분배 범위(모든 구독 계획)는 본문의 공통 설명이 맡는다. */
function startPromptCopy({
  timing,
  startDateISO,
}: StartPrompt): { title: string; note: string } {
  const start = formatMonthDay(startDateISO);
  return timing === "early"
    ? {
        title: "일찍 시작하셨네요 — 오늘 기준으로 다시 분배할까요?",
        note: `계획은 ${start}부터 시작하는 일정이라 그날까지는 캘린더가 비어 있어요. 그대로 두고 그날부터 따라가도 되고, 오늘부터 시작하려면 다시 분배하세요.`,
      }
    : {
        title: "계획 시작일이 지났어요 — 오늘 기준으로 다시 분배할까요?",
        note: `계획 시작일(${start})이 이미 지나 앞부분이 밀려 있어요. 오늘부터 시험 전날까지 다시 나눌 수 있어요.`,
      };
}

/**
 * 재분배 분포 모델 메타데이터 (라디오 옵션 렌더용).
 * 배열 순서는 REPLAN_MODELS(["even","front","back","pyramid"])와 1:1 일치해야 한다.
 * bars = 5칸 막대 스파크라인의 상대 높이(1~5) — 분포 모양을 직관적으로 보여주는 장식.
 */
const REPLAN_MODEL_META: {
  model: ReplanModel;
  label: string;
  desc: string;
  bars: [number, number, number, number, number];
}[] = [
  {
    model: "even",
    label: "균등",
    desc: "매일 비슷한 분량으로",
    bars: [3, 3, 3, 3, 3],
  },
  {
    model: "front",
    label: "초기 열정형",
    desc: "초반에 많이, 뒤로 갈수록 적게",
    bars: [5, 4, 3, 2, 1],
  },
  {
    model: "back",
    label: "후기 몰아치기형",
    desc: "초반엔 적게, 시험이 가까울수록 많이",
    bars: [1, 2, 3, 4, 5],
  },
  {
    model: "pyramid",
    label: "피라미드형",
    desc: "초반·막판은 적게, 중반에 집중",
    bars: [1, 3, 5, 3, 1],
  },
];
// REPLAN_MODELS 순서와 메타 배열 순서가 어긋나면 빌드 시점에 잡도록 정렬 보증.
const REPLAN_MODEL_OPTIONS = REPLAN_MODELS.map(
  (m) => REPLAN_MODEL_META.find((meta) => meta.model === m)!,
);

/** 커서 X가 보드(칼럼들)의 좌/우 가장자리 EDGE 안이면 -1/+1, 아니면 0. 가장자리 자동이동 방향. */
function edgeDir(cursorX: number): -1 | 0 | 1 {
  const cols = document.querySelectorAll<HTMLElement>("[data-day-column]");
  if (cols.length === 0) return 0;
  const left = cols[0].getBoundingClientRect().left;
  const right = cols[cols.length - 1].getBoundingClientRect().right;
  const EDGE = 44;
  if (cursorX > right - EDGE) return 1;
  if (cursorX < left + EDGE) return -1;
  return 0;
}

/**
 * 학습 일정 캘린더 본체 (JCG-2310).
 *
 * 주(week=5일 슬라이딩 윈도우)/월(month) 뷰 토글. DnD 일정 조정은 week 뷰에서만(월은 읽기 전용 개요).
 * week 뷰는 캘린더 주(일~토 7칸)가 아니라 anchorISO 부터 5일 윈도우 — 헤더 ‹ › 화살표(±1일)·
 * "오늘" 버튼으로 하루씩 옮겨가며 본다. windowStart 는 계획 범위 근처로 clamp.
 * 헤더: 뷰 토글·오늘 버튼·좌우 네비·범례(+계획 필터). 통합 색 구분(이론=blue/코딩=green).
 *
 * DnD: @dnd-kit DndContext(주 뷰만 활성)는 "드래그 시작 판정"(PointerSensor 6px=클릭/탭 분리)과
 * 원본 식별까지만 맡고, 이동/드롭은 onDragStart 에서 document 에 직접 부착한 pointer 리스너가 처리한다
 * (JCG-2458). 덕분에 가장자리 자동이동이 원본 칼럼을 지나쳐 dnd-kit 이 onDragCancel 을 쏴도 드롭이
 * 정상 완료돼, 계획 범위 어느 날짜로든(±4일 제한 없이) 옮길 수 있다. 카드 "⋯" 메뉴가 키보드/모바일
 * 이동 대안. 드래그 중엔 떠 있는 클론(body portal)이 커서를 따르고, 드롭 대상 칼럼/위치는 freezeColumns
 * 스냅샷 기준 dropTargetFrozen(커서 좌표)으로 찾는다. 커서가 보드 좌/우 가장자리에 머물면 윈도우를
 * 자동으로 ±1일씩 민다(clamp 없음). drop = reorder(낙관적, useScheduleData).
 */
export default function ScheduleCalendar({ data }: { data: ScheduleData }) {
  // todayISO 는 data 훅이 아니라 여기서 KST 로 1회 계산해 모든 자식에 내린다.
  const todayISO = useToday();
  // 구독 직후 재분배 안내. 구독이 끝나면 아래 분기가 StartPlanCTA → SubscribedCalendar 로 바뀌므로
  // 상태는 두 화면을 아우르는 여기서 들고, SubscribedCalendar 가 재분배 다이얼로그로 보여준다.
  const [startPrompt, setStartPrompt] = useState<StartPrompt | null>(null);
  const { subscribe } = data;
  const startPlan = useCallback(
    async (planId: StudyPlanId) => {
      const result = await subscribe(planId, todayISO);
      if (result && result.timing !== "onTime")
        setStartPrompt({
          timing: result.timing,
          startDateISO: result.startDateISO,
        });
      return result !== null;
    },
    [subscribe, todayISO],
  );
  const clearStartPrompt = useCallback(() => setStartPrompt(null), []);

  // 구독 0개(range 없음) → 정규 날짜 미리보기 + 시작 CTA. 캘린더 훅을 호출하지 않도록 여기서 분기.
  if (!data.range) {
    if (data.readOnly) {
      return (
        <div className="rounded-xl border border-border bg-card px-6 py-12 text-center">
          <p className="text-sm font-medium text-foreground">
            이 회차에 시작한 학습 계획이 없어요.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            이전 회차는 당시 저장된 학습 일정만 조회할 수 있어요.
          </p>
        </div>
      );
    }

    return (
      <StartPlanCTA
        startable={data.startable}
        todayISO={todayISO}
        onStart={startPlan}
      />
    );
  }

  return (
    <SubscribedCalendar
      data={data}
      range={data.range}
      todayISO={todayISO}
      onStart={startPlan}
      startPrompt={startPrompt}
      onStartPromptHandled={clearStartPrompt}
    />
  );
}

/** 구독 1+(range 보장) — 주/월 캘린더 본체. 모든 캘린더 훅은 여기서만 호출(조건부 호출 없음). */
function SubscribedCalendar({
  data,
  range,
  todayISO,
  onStart,
  startPrompt,
  onStartPromptHandled,
}: {
  data: ScheduleData;
  range: NonNullable<ScheduleData["range"]>;
  todayISO: string;
  onStart: (planId: StudyPlanId) => Promise<boolean>;
  startPrompt: StartPrompt | null;
  onStartPromptHandled: () => void;
}) {
  const { subscribedIds } = data;

  const [view, setView] = useState<ViewMode>("week");
  const [filter, setFilter] = useState<PlanFilter>("all");
  // 앵커: 주 뷰는 5일 윈도우의 시작일(일요일 스냅 없음), 월 뷰는 그 달 1일 기준. 오늘로 초기화.
  // 이전 회차는 현재 날짜가 계획 밖이므로 해당 회차 범위 안의 가장 가까운 날짜에서 시작한다.
  const [anchorISO, setAnchorISO] = useState<string>(() =>
    data.readOnly ? clampISO(todayISO, range.minISO, range.maxISO) : todayISO,
  );

  const [dropIndicator, setDropIndicator] = useState<DropIndicator | null>(
    null,
  );
  // 드래그 중인 카드 — 떠 있는 클론을 body 로 portal. rect=시작 시 원본 위치/폭(클론 기준점).
  // 위치는 dnd-kit DragOverlay 대신 e.delta 로 직접 갱신(이 환경에선 dnd-kit 내부 위치계산이 부정확).
  const [drag, setDrag] = useState<{
    task: ScheduledTask;
    rect: { left: number; top: number; width: number };
  } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  // 가장자리 자동이동: 커서가 보드 끝에 머무는 동안 600ms마다 1일씩 윈도우 이동. dir -1/0/1 + 타이머.
  const panDirRef = useRef<-1 | 0 | 1>(0);
  const panTimerRef = useRef<number | null>(null);
  // 자체 포인터 드래그 세션(JCG-2458) — dnd-kit 수명주기와 분리. dnd-kit 은 "드래그 시작 판정"과
  // 원본 식별에만 쓰고, 이동/드롭은 document 에 직접 부착한 pointer 리스너가 처리한다. 그래서
  // 가장자리 자동이동으로 원본 칼럼이 언마운트돼 dnd-kit 이 onDragCancel 을 쏴도 드롭이 완료된다.
  const dragSessionRef = useRef<{
    task: ScheduledTask;
    activeUid: string;
    // 드래그를 시작한 포인터 ID — 자체 리스너가 이 포인터의 이벤트만 처리(멀티터치/펜 혼용 시
    // 두 번째 포인터의 up/cancel 이 드롭을 조기 확정·취소하는 것 방지). 포인터 외 입력이면 null.
    pointerId: number | null;
    startCursor: { x: number; y: number };
    lastCursor: { x: number; y: number };
    handled: boolean;
    cleanup: () => void;
  } | null>(null);
  // 자체 pointerup 이 드롭을 계산할 때 최신 tasksByDate/data 를 읽도록 ref 로 보관(리스너 stale 방지).
  const dropDepsRef = useRef<{
    tasksByDate: Map<string, ScheduledTask[]>;
    data: ScheduleData;
  } | null>(null);
  // 월 뷰에서 날짜를 누르면 그 하루만 모달로(체크·순서변경 DnD). null=닫힘.
  const [openDayISO, setOpenDayISO] = useState<string | null>(null);
  // "오늘 기준 다시 분배" 확인 다이얼로그. 툴바 버튼(manual)과 구독 직후 안내(start, 부모가 들고
  // 있는 startPrompt)가 같은 다이얼로그를 열므로 한 값으로 합쳐 제목·안내 줄·취소 문구를 가른다.
  const [replanRequested, setReplanRequested] = useState(false);
  const replanDialog: ReplanDialogState = startPrompt
    ? { reason: "start", ...startPrompt }
    : replanRequested
      ? { reason: "manual" }
      : null;
  const closeReplan = useCallback(() => {
    setReplanRequested(false);
    onStartPromptHandled();
  }, [onStartPromptHandled]);
  const promptCopy =
    replanDialog?.reason === "start" ? startPromptCopy(replanDialog) : null;
  // 날짜 이동·재정렬을 정규 학습 일정으로 되돌리는 확인 다이얼로그.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPlanIds, setResetPlanIds] = useState<StudyPlanId[]>([]);
  const [stopOpen, setStopOpen] = useState(false);
  // 재분배 분포 모델 선택(라디오). 기본 = 균등("even").
  const [replanModel, setReplanModel] = useState<ReplanModel>("even");
  // 드래그 시작 시 카드 위치 스냅샷(jitter 방지 — 삽입 위치를 고정값으로 계산).
  const frozenRef = useRef<FrozenColumns>(new Map());
  // 직전 드롭 카드(FLIP 슬라이드 제외 → 떨어뜨린 자리에 안착). WeekBoard 칼럼들이 공유.
  const lastDraggedRef = useRef<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  // 필터 적용된 task 만 날짜별로 그룹(주/월 셀 채우기).
  const tasksByDate = useMemo(() => {
    const map = new Map<string, ScheduledTask[]>();
    for (const t of data.allTasks) {
      if (filter !== "all" && t.planId !== filter) continue;
      const arr = map.get(t.effectiveDateISO);
      if (arr) arr.push(t);
      else map.set(t.effectiveDateISO, [t]);
    }
    // 같은 날 안 정렬: 사용자 재정렬(sortOrder) 우선, 없으면 기본 순서(계획·Day·인덱스).
    for (const arr of map.values()) {
      arr.sort((a, b) => taskSortValue(a) - taskSortValue(b));
    }
    return map;
  }, [data.allTasks, filter]);
  // 자체 pointer 리스너가 드롭 계산 시 최신 값을 읽도록 ref 갱신(stale 클로저 방지).
  // 렌더 중 ref 쓰기는 react-hooks/refs 위반이라 effect 로 매 렌더 후 갱신한다.
  useEffect(() => {
    dropDepsRef.current = { tasksByDate, data };
  });

  const actions = useMemo<TaskCardActions>(
    () => ({
      onToggle: (task, done) => void data.toggle(task, done),
      onSkip: (task, skipped) => void data.skip(task, skipped),
      onMove: (task, toISO) => void data.move(task, toISO),
    }),
    [data],
  );

  // ── 5일 윈도우 ──
  // windowStart 는 anchor 그대로(주 스냅 없음). 계획 범위(min~max) 근처로 clamp 해 빈 화면으로
  // 멀리 못 가게 한다: 시작일을 [min-2 .. max] 사이로 제한(끝쪽에서도 윈도우가 너무 비지 않게).
  const windowMinISO = addDaysISO(range.minISO, -2);
  const windowMaxISO = range.maxISO;
  const windowStartISO = clampISO(anchorISO, windowMinISO, windowMaxISO);
  const windowDayRange = useWeekDayRange(data, windowStartISO);

  // 드래그 중 윈도우가 가장자리 자동이동으로 바뀌면 고정 스냅샷(frozen)을 새 칼럼 기준으로 다시 잡는다.
  useLayoutEffect(() => {
    if (drag) frozenRef.current = freezeColumns();
  }, [windowStartISO, drag]);
  // 언마운트 시 자동이동 타이머 + 자체 드래그 리스너 정리(드래그 중 페이지 이동 등 누수 방지).
  useEffect(
    () => () => {
      if (panTimerRef.current !== null) clearInterval(panTimerRef.current);
      dragSessionRef.current?.cleanup();
    },
    [],
  );

  // ── DnD 핸들러 ──
  const clearPan = () => {
    panDirRef.current = 0;
    if (panTimerRef.current !== null) {
      clearInterval(panTimerRef.current);
      panTimerRef.current = null;
    }
  };

  // 드롭 적용 — 자체 pointerup(또는 dnd-kit onDragEnd 안전망)이 마지막 커서로 호출한다.
  // 최신 tasksByDate/data 는 dropDepsRef 로 읽어 리스너 stale 클로저를 피한다.
  const applyDrop = (
    task: ScheduledTask,
    cursor: { x: number; y: number },
    activeUid: string,
  ) => {
    const deps = dropDepsRef.current;
    if (!deps) return;
    const { tasksByDate, data } = deps;
    const target = dropTargetFrozen(cursor, frozenRef.current, activeUid);
    if (!target) return;
    const siblings = (tasksByDate.get(target.toISO) ?? []).filter(
      (t) => t.uid !== task.uid,
    );
    const newSortOrder = computeDropOrder(target, siblings, task);
    // 같은 칼럼 재정렬이면 날짜(movedToDate) 그대로, 다른 칼럼이면 그 날짜로 이동.
    const newMovedToDate =
      target.toISO === task.effectiveDateISO ? task.movedToDate : target.toISO;
    // 같은 칼럼인데 순서가 안 바뀌면 no-op — 불필요한 override 기록·서버 reorder 호출 방지(JCG-2310 리뷰).
    if (
      target.toISO === task.effectiveDateISO &&
      isUnchangedColumnOrder(
        task,
        newSortOrder,
        tasksByDate.get(task.effectiveDateISO) ?? [],
      )
    )
      return;
    lastDraggedRef.current = task.uid; // 이 카드는 FLIP 슬라이드 제외(드롭 지점 안착)
    void data.reorder(task, newMovedToDate, newSortOrder);
  };

  // 드래그 세션 종료(공용) — apply=true 면 마지막 커서로 드롭 적용. handled 플래그로 중복 방지
  // (자체 pointerup 과 dnd-kit onDragEnd 안전망이 둘 다 호출해도 실제 처리는 한 번).
  const finishDrag = (apply: boolean) => {
    const session = dragSessionRef.current;
    if (!session || session.handled) return;
    session.handled = true;
    session.cleanup();
    dragSessionRef.current = null;
    setDropIndicator(null);
    setDrag(null);
    clearPan();
    if (apply) applyDrop(session.task, session.lastCursor, session.activeUid);
  };

  const handleDragStart = (e: DragStartEvent) => {
    // 이전 세션 잔재 정리(멱등) — 누수된 리스너/타이머 누적 방지.
    dragSessionRef.current?.cleanup();
    dragSessionRef.current = null;
    clearPan();

    frozenRef.current = freezeColumns();
    lastDraggedRef.current = null; // FLIP 제외 대상 초기화(직전 드롭 잔재 제거)

    const task = (e.active.data.current?.task as ScheduledTask) ?? null;
    const act = e.activatorEvent;
    const startCursor =
      act && "clientX" in act
        ? { x: (act as MouseEvent).clientX, y: (act as MouseEvent).clientY }
        : null;
    // 드래그를 시작한 포인터 ID(자체 리스너가 이 포인터만 추적). 포인터 이벤트가 아니면 null.
    const pointerId =
      act && "pointerId" in act ? (act as PointerEvent).pointerId : null;
    const r = task
      ? document
          .querySelector(`[data-task-uid="${task.uid}"]`)
          ?.getBoundingClientRect()
      : undefined;
    // 포인터 이벤트가 아니거나(키보드 등) 원본 rect 를 못 잡으면 자체 추적 불가 → 드래그 미시작.
    if (!task || !r || !startCursor) {
      setDrag(null);
      return;
    }
    setDrag({ task, rect: { left: r.left, top: r.top, width: r.width } });

    const activeUid = String(e.active.id);

    // ── 자체 포인터 리스너(capture 단계: dnd-kit 자체 document 리스너보다 먼저 발화) ──
    // 여기서부터 드래그를 직접 추적·완료한다. dnd-kit 은 시작 판정/원본 식별까지만 관여.
    const onPointerMove = (ev: PointerEvent) => {
      const session = dragSessionRef.current;
      if (!session) return;
      if (session.pointerId !== null && ev.pointerId !== session.pointerId)
        return; // 다른 포인터(멀티터치)의 이동은 무시
      const cursor = { x: ev.clientX, y: ev.clientY };
      session.lastCursor = cursor;
      // 클론 위치를 시작 커서 기준 이동량으로 직접 갱신(re-render 없이 ref 로). 시작 rect + 이동량.
      if (overlayRef.current) {
        overlayRef.current.style.transform = `translate3d(${
          cursor.x - session.startCursor.x
        }px, ${cursor.y - session.startCursor.y}px, 0)`;
      }
      // 커서 X로 보드 좌/우 가장자리 판정 → 자동이동 방향 갱신(실제 이동은 타이머가 수행).
      panDirRef.current = edgeDir(cursor.x);
      // 가장자리(자동이동 중)에선 삽입 인디케이터를 숨긴다 — 엉뚱한 칼럼에 선이 남지 않게.
      if (panDirRef.current !== 0) {
        setDropIndicator(null);
        return;
      }
      setDropIndicator(
        dropTargetFrozen(cursor, frozenRef.current, session.activeUid),
      );
    };
    const onPointerUp = (ev: PointerEvent) => {
      const session = dragSessionRef.current;
      if (session) {
        if (session.pointerId !== null && ev.pointerId !== session.pointerId)
          return; // 다른 포인터의 up 은 드롭을 확정하지 않음
        session.lastCursor = { x: ev.clientX, y: ev.clientY };
      }
      finishDrag(true); // 마지막 커서로 드롭 — dnd-kit 이 cancel 했어도 정상 완료(JCG-2458)
    };
    const onPointerCancel = (ev: PointerEvent) => {
      const session = dragSessionRef.current;
      if (
        session &&
        session.pointerId !== null &&
        ev.pointerId !== session.pointerId
      )
        return; // 다른 포인터의 cancel 은 무시
      finishDrag(false);
    };
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") finishDrag(false);
    };

    const cleanup = () => {
      document.removeEventListener("pointermove", onPointerMove, true);
      document.removeEventListener("pointerup", onPointerUp, true);
      document.removeEventListener("pointercancel", onPointerCancel, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
    document.addEventListener("pointermove", onPointerMove, true);
    document.addEventListener("pointerup", onPointerUp, true);
    document.addEventListener("pointercancel", onPointerCancel, true);
    document.addEventListener("keydown", onKeyDown, true);

    dragSessionRef.current = {
      task,
      activeUid,
      pointerId,
      startCursor,
      lastCursor: startCursor,
      handled: false,
      cleanup,
    };

    // 커서가 보드 끝에 머무는 동안 600ms마다 1일씩 윈도우를 민다(클론은 portal이라 안 튄다).
    // clamp 없음(JCG-2458): 원본 칼럼이 화면 밖으로 나가도 자체 pointerup 이 드롭을 완료하므로,
    // 계획 범위(windowMin~Max, shiftWindow 가 clamp) 안 어느 날짜로든 옮길 수 있다.
    panDirRef.current = 0;
    panTimerRef.current = window.setInterval(() => {
      const dir = panDirRef.current;
      if (dir === 0) return;
      setDropIndicator(null);
      shiftWindow(dir);
    }, 600);
  };

  // ── 네비게이션 ──
  // week 뷰: 윈도우를 days(±)만큼 이동(헤더 ‹ › 화살표=±5일=다음/이전 5일, 보드 스와이프=끈 만큼).
  // clamp 로 빈 화면 방지. month 뷰: 달 단위 이동.
  const shiftWindow = (days: number) =>
    setAnchorISO((cur) =>
      clampISO(addDaysISO(cur, days), windowMinISO, windowMaxISO),
    );
  const goPrev = () => {
    if (view === "week") shiftWindow(-WINDOW_DAYS);
    else setAnchorISO((cur) => addMonthsAnchor(cur, -1));
  };
  const goNext = () => {
    if (view === "week") shiftWindow(WINDOW_DAYS);
    else setAnchorISO((cur) => addMonthsAnchor(cur, 1));
  };
  // 오늘 버튼: week 뷰에서는 오늘을 2번째 칼럼에 두어(=시작일 오늘-1) 어제·내일 맥락을 함께 보여준다.
  const goToday = () =>
    setAnchorISO(
      view === "week"
        ? clampISO(addDaysISO(todayISO, -1), windowMinISO, windowMaxISO)
        : todayISO,
    );

  // 월 셀 클릭: 그 날짜를 윈도우 가운데(3번째 칼럼, =시작일 날짜-2)에 두고 week 뷰로 전환.
  const pickWeek = (dateISO: string) => {
    setAnchorISO(clampISO(addDaysISO(dateISO, -2), windowMinISO, windowMaxISO));
    setView("week");
  };

  // 5일 윈도우 라벨: "7/6 – 7/10" 류. Day 범위·주차(windowDayRange)는 헤더 보조 라벨로.
  const windowRangeLabel = `${formatMonthDay(windowStartISO)} – ${formatMonthDay(
    addDaysISO(windowStartISO, WINDOW_DAYS - 1),
  )}`;

  // 남은(미완료) 일정 존재 여부 — "다시 분배"는 밀렸든 아니든 남은 게 있으면 쓸 수 있다(균등 재배치).
  // 재분배는 필터와 무관하게 모든 구독 계획을 대상으로 하므로 filter 를 보지 않는다.
  const hasRemaining = useMemo(
    () => data.allTasks.some((t) => !t.done && !t.skipped),
    [data.allTasks],
  );
  const resettablePlanIds = useMemo(
    () =>
      subscribedIds.filter((planId) => {
        const subscribed = data.subscribed.find((s) => s.planId === planId);
        if (!subscribed) return false;
        const hasCustomStartDate =
          subscribed.startDateISO !== subscribed.plan.startDateISO;
        return (
          hasCustomStartDate ||
          data.allTasks.some(
            (t) =>
              t.planId === planId &&
              (t.movedToDate !== null || t.sortOrder !== null),
          )
        );
      }),
    [data.allTasks, data.subscribed, subscribedIds],
  );
  const hasResettableChanges = resettablePlanIds.length > 0;
  const selectedResetPlanIds = useMemo(
    () => resetPlanIds.filter((planId) => resettablePlanIds.includes(planId)),
    [resetPlanIds, resettablePlanIds],
  );

  // "⋯ → 다른 날로 이동" 후보(+ 오늘 표시).
  // 윈도우가 5일로 좁아졌으므로 후보는 윈도우 주변으로 넉넉히(windowStart-3 ~ +7, 약 11일) 펼쳐
  // 좁은 윈도우에서도 멀리 옮길 수 있게 한다. month 뷰는 오늘 주변 11일 기준.
  const moveTargets = useMemo<MoveTarget[]>(() => {
    const base =
      view === "week"
        ? addDaysISO(windowStartISO, -3)
        : addDaysISO(todayISO, -3);
    return Array.from({ length: WINDOW_DAYS + 6 }, (_, i) => {
      const iso = addDaysISO(base, i);
      return { iso, label: formatMonthDay(iso), isToday: iso === todayISO };
    });
  }, [view, windowStartISO, todayISO]);

  const monthLabel = formatYearMonth(anchorISO);
  const weekBoard = (
    <WeekBoard
      windowStartISO={windowStartISO}
      todayISO={todayISO}
      tasksByDate={tasksByDate}
      moveTargets={moveTargets}
      actions={actions}
      dropIndicator={data.readOnly ? null : dropIndicator}
      flipExcludeRef={lastDraggedRef}
      onShiftDays={shiftWindow}
      dragging={!data.readOnly && drag !== null}
      readOnly={data.readOnly}
    />
  );

  return (
    <div className="space-y-4">
      <ScheduleHeader
        view={view}
        setView={setView}
        filter={filter}
        setFilter={setFilter}
        subscribedIds={subscribedIds}
        onPrev={goPrev}
        onNext={goNext}
        onToday={goToday}
        rangeLabel={view === "week" ? windowRangeLabel : monthLabel}
        subLabel={view === "week" ? windowDayRange : null}
        showFilter={subscribedIds.length > 1}
        showToday={!data.readOnly}
      />

      {!data.readOnly && data.startable.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2">
          <span className="text-xs text-muted-foreground">
            아직 시작 안 한 계획이 있어요:
          </span>
          {data.startable.map(({ planId }) => (
            <button
              key={planId}
              type="button"
              onClick={() => void onStart(planId)}
              className={cn(
                "inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
                PLAN_START_PILL[planId],
              )}
            >
              <Plus className="h-3 w-3" aria-hidden />
              {STUDY_PLAN_LABELS[planId]} 시작
            </button>
          ))}
        </div>
      )}

      {!data.readOnly && subscribedIds.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap justify-end gap-2">
            {view === "week" && hasResettableChanges && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setResetPlanIds(resettablePlanIds);
                  setResetOpen(true);
                }}
              >
                <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden />
                원래 감자 일정으로 복귀
              </Button>
            )}
            {view === "week" && hasRemaining && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setReplanRequested(true)}
                className="border-amber-400 text-amber-700 hover:bg-amber-50 dark:border-amber-600 dark:text-amber-300 dark:hover:bg-amber-950/40"
              >
                <CalendarSync className="mr-1.5 h-4 w-4" aria-hidden />
                오늘 기준 다시 분배
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={data.writePending}
              onClick={() => setStopOpen(true)}
              className="text-muted-foreground"
            >
              <CircleStop className="mr-1.5 h-4 w-4" aria-hidden />
              계획 그만두기
            </Button>
          </div>
        </div>
      )}

      {view === "week" ? (
        data.readOnly ? (
          weekBoard
        ) : (
          <DndContext
            sensors={sensors}
            // 페이지 오토스크롤 시 고정 좌표(frozen)가 어긋나 드롭이 빗나가는 걸 막는다.
            autoScroll={false}
            // dnd-kit 은 드래그 시작 판정·원본 식별까지만. 이동/드롭은 handleDragStart 가 부착하는
            // 자체 pointer 리스너가 처리한다(JCG-2458). onDragEnd 는 안전망 — 자체 pointerup 이 이미
            // 처리했으면 handled 플래그로 무시. onDragCancel 은 일부러 두지 않는다: 자동이동으로
            // 원본이 언마운트돼 취소가 와도 드롭을 이어가야 하므로(실제 취소는 자체 pointercancel/Escape).
            onDragStart={handleDragStart}
            onDragEnd={() => finishDrag(true)}
          >
            {weekBoard}
            {/* 떠 있는 드래그 클론(DragClone=body portal). 위치 갱신은 자체 pointermove 리스너가 담당. */}
            {drag && (
              <DragClone rect={drag.rect} overlayRef={overlayRef}>
                <TaskCard
                  task={drag.task}
                  actions={actions}
                  moveTargets={moveTargets}
                  overlay
                />
              </DragClone>
            )}
          </DndContext>
        )
      ) : (
        <MonthGrid
          monthAnchorISO={anchorISO}
          todayISO={todayISO}
          tasksByDate={tasksByDate}
          rangeMinISO={range.minISO}
          rangeMaxISO={range.maxISO}
          onOpenDay={setOpenDayISO}
        />
      )}

      <p className="text-center text-[11px] text-muted-foreground">
        {data.readOnly
          ? view === "week"
            ? "‹ › 화살표(±5일)·보드를 좌우로 스와이프해 이전 회차 일정을 조회할 수 있어요."
            : "날짜를 누르면 그 날의 이전 회차 일정을 자세히 볼 수 있어요."
          : view === "week"
            ? "‹ › 화살표(±5일)·보드를 좌우로 스와이프해 날짜를 옮기고, 카드는 다른 날 칸으로 끌거나 ⋯ 메뉴로 옮겨요."
            : "날짜를 누르면 그 날 일정을 모달로 열어 체크·순서를 바꿀 수 있어요."}
      </p>

      <DayDetailDialog
        dateISO={openDayISO}
        tasks={openDayISO ? (tasksByDate.get(openDayISO) ?? []) : []}
        moveTargets={moveTargets}
        actions={actions}
        onReorder={data.reorder}
        readOnly={data.readOnly}
        onClose={() => setOpenDayISO(null)}
        onViewWeek={(iso) => {
          setOpenDayISO(null);
          pickWeek(iso);
        }}
      />

      {!data.readOnly && (
        <>
          {/* 계획 그만두기(구독 해제, JCG-3677). 오버라이드는 보존돼 다시 시작하면 복원된다. */}
          <Dialog open={stopOpen} onOpenChange={setStopOpen}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>어떤 계획을 그만둘까요?</DialogTitle>
                <DialogDescription>
                  그만둔 계획은 학습 일정에서 빠지고, 학습 페이지에서 일정
                  완료나 다음 일정을 더 이상 묻지 않아요. 출석 기록은 그대로
                  남고, 다시 시작하면 지금까지의 진행 기록이 복원돼요.
                </DialogDescription>
              </DialogHeader>
              <StopPlanList
                subscribedIds={subscribedIds}
                disabled={data.writePending}
                onStop={async (planId) => {
                  const ok = await data.unsubscribe(planId);
                  if (ok && subscribedIds.length <= 1) setStopOpen(false);
                }}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStopOpen(false)}
                >
                  닫기
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* 시작일·날짜 이동·재정렬만 원래 감자 일정 기준으로 되돌린다. 완료/건너뛰기 상태는 유지한다. */}
          <Dialog open={resetOpen} onOpenChange={setResetOpen}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  어떤 계획을 원래 감자 일정으로 되돌릴까요?
                </DialogTitle>
                <DialogDescription>
                  선택한 계획을 원래 감자 일정으로 되돌려요. 완료·건너뛰기
                  상태는 그대로 유지됩니다.
                </DialogDescription>
              </DialogHeader>
              <ResetPlanPicker
                subscribedIds={subscribedIds}
                resettablePlanIds={resettablePlanIds}
                selectedPlanIds={selectedResetPlanIds}
                onChange={setResetPlanIds}
                examSessionId={data.selectedExamSessionId}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setResetOpen(false)}
                >
                  취소
                </Button>
                <Button
                  type="button"
                  disabled={selectedResetPlanIds.length === 0}
                  onClick={() => {
                    void data.resetSchedule(selectedResetPlanIds);
                    setResetOpen(false);
                  }}
                >
                  <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden />
                  선택한 일정 복귀
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* "오늘 기준 다시 분배" 확인 다이얼로그 (alert-dialog 미설치 → shadcn Dialog 사용).
              확인 시 data.replan(todayISO, replanModel) — 낙관적 업데이트·toast·서버 재동기화는 훅 내부에서 처리. */}
          <Dialog
            open={replanDialog !== null}
            onOpenChange={(open) => {
              if (!open) closeReplan();
            }}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {promptCopy?.title ?? "오늘 기준으로 다시 분배할까요?"}
                </DialogTitle>
                <DialogDescription>
                  {promptCopy && (
                    <span className="mb-2 block font-medium text-amber-700 dark:text-amber-400">
                      {promptCopy.note}
                    </span>
                  )}
                  구독 중인 모든 계획의 완료하지 않은 일정을 오늘부터 시험
                  전날까지 다시 배치해요. 완료한 일정과 건너뛴 일정은 날짜와
                  상태를 그대로 유지해요.
                  미완료 일정은 직접 옮긴 날짜와 순서도 선택한 분배 방식으로
                  다시 정해져요. 같은 과목도 1회독을 마치고 2회독이 남아 있다면
                  미완료인 2회독 일정만 다시 배치해요.
                </DialogDescription>
              </DialogHeader>
              <ReplanModelPicker
                value={replanModel}
                onChange={setReplanModel}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeReplan}>
                  {promptCopy ? "그대로 두기" : "취소"}
                </Button>
                <Button
                  type="button"
                  className="bg-amber-500 text-white hover:bg-amber-600 dark:bg-amber-400 dark:text-amber-950 dark:hover:bg-amber-300"
                  onClick={() => {
                    void data.replan(todayISO, replanModel);
                    closeReplan();
                  }}
                >
                  <CalendarSync className="mr-1.5 h-4 w-4" aria-hidden />
                  다시 분배
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

// ── 계획 그만두기 ─────────────────────────────────────────────────────────────

function StopPlanList({
  subscribedIds,
  disabled,
  onStop,
}: {
  subscribedIds: StudyPlanId[];
  disabled: boolean;
  onStop: (planId: StudyPlanId) => Promise<void>;
}) {
  return (
    <ul className="grid gap-2">
      {subscribedIds.map((planId) => (
        <li
          key={planId}
          className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2.5"
        >
          <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
            <span
              aria-hidden
              className={cn("h-2 w-2 shrink-0 rounded-full", PLAN_LEGEND_DOT[planId])}
            />
            {STUDY_PLAN_LABELS[planId]}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => void onStop(planId)}
            aria-label={`${STUDY_PLAN_LABELS[planId]} 그만두기`}
          >
            그만두기
          </Button>
        </li>
      ))}
    </ul>
  );
}

// ── 원래 감자 일정 복귀 범위 선택 ───────────────────────────────────────────────

function ResetPlanPicker({
  subscribedIds,
  resettablePlanIds,
  selectedPlanIds,
  onChange,
  examSessionId,
}: {
  subscribedIds: StudyPlanId[];
  resettablePlanIds: StudyPlanId[];
  selectedPlanIds: StudyPlanId[];
  onChange: (planIds: StudyPlanId[]) => void;
  examSessionId: string;
}) {
  const togglePlan = (planId: StudyPlanId, checked: boolean) => {
    if (checked) {
      onChange(
        STUDY_PLAN_IDS.filter(
          (id) => id === planId || selectedPlanIds.includes(id),
        ),
      );
      return;
    }
    onChange(selectedPlanIds.filter((id) => id !== planId));
  };

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-semibold text-foreground">
        적용 범위
      </legend>
      <div className="grid gap-2">
        {subscribedIds.map((planId) => {
          const plan = getStudyPlan(planId, examSessionId);
          const resettable = resettablePlanIds.includes(planId);
          const checked = selectedPlanIds.includes(planId);
          const checkboxId = `reset-plan-${planId}`;
          const descriptionId = `reset-plan-${planId}-description`;
          return (
            <div
              key={planId}
              className={cn(
                "flex items-start gap-3 rounded-lg border border-border bg-card px-3 py-2.5 transition-colors",
                "hover:bg-accent",
              )}
            >
              <Checkbox
                id={checkboxId}
                checked={checked}
                disabled={!resettable}
                aria-describedby={descriptionId}
                onCheckedChange={(value) => togglePlan(planId, value === true)}
                className="mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                  <label
                    htmlFor={checkboxId}
                    className={cn(
                      "min-w-0 flex-1",
                      resettable
                        ? "cursor-pointer"
                        : "cursor-not-allowed opacity-60",
                    )}
                  >
                    <span className="block text-sm font-medium text-foreground">
                      {STUDY_PLAN_LABELS[planId]}
                    </span>
                    <span
                      id={descriptionId}
                      className="mt-0.5 block text-xs text-muted-foreground"
                    >
                      {resettable
                        ? "변경된 시작일·날짜·순서를 원래 감자 일정 기준으로 복귀"
                        : "현재 되돌릴 시작일·날짜 변경이 없음"}
                    </span>
                  </label>
                  {plan && (
                    <Link
                      href={plan.pagePath}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md text-xs font-semibold text-amber-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-amber-300"
                    >
                      원래 일정 보기
                      <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        {selectedPlanIds.length === 0
          ? "한 개 이상 선택해야 원래 감자 일정으로 복귀할 수 있어요."
          : "선택하지 않은 계획은 현재 일정 그대로 둡니다."}
      </p>
    </fieldset>
  );
}

// ── 재분배 분포 모델 선택 ──────────────────────────────────────────────────────

/**
 * 재분배 분포 모델 라디오 그룹 (shadcn RadioGroup 미설치 → WAI-ARIA radio 패턴 직접 구현).
 *
 * - `role="radiogroup"` 컨테이너 + 각 옵션 `role="radio"` 버튼(aria-checked).
 * - roving tabindex: 선택된 옵션만 Tab 진입 가능(tabIndex=0), 나머지는 -1.
 * - ↑/← 이전, ↓/→ 다음(양끝 wrap), Home/End 처음/끝. 이동 즉시 선택+포커스(라디오 표준).
 * - 각 옵션 = 라벨 + 한 줄 설명 + 5칸 막대 스파크라인(분포 모양 장식, aria-hidden).
 */
function ReplanModelPicker({
  value,
  onChange,
}: {
  value: ReplanModel;
  onChange: (m: ReplanModel) => void;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const focusAt = (i: number) => {
    onChange(REPLAN_MODEL_OPTIONS[i].model);
    refs.current[i]?.focus();
  };
  const onKeyDown = (e: ReactKeyboardEvent, i: number) => {
    const last = REPLAN_MODEL_OPTIONS.length - 1;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      focusAt(i === last ? 0 : i + 1);
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      focusAt(i === 0 ? last : i - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusAt(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusAt(last);
    }
  };
  return (
    <div role="radiogroup" aria-label="재분배 분포 방식" className="grid gap-2">
      {REPLAN_MODEL_OPTIONS.map((opt, i) => {
        const selected = value === opt.model;
        return (
          <button
            key={opt.model}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(opt.model)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              "flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
              selected
                ? "border-amber-400 bg-amber-50 dark:border-amber-600 dark:bg-amber-950/40"
                : "border-border hover:bg-accent",
            )}
          >
            <ReplanSparkline bars={opt.bars} active={selected} />
            <span className="min-w-0 flex-1">
              <span
                className={cn(
                  "block text-sm font-semibold",
                  selected
                    ? "text-amber-700 dark:text-amber-300"
                    : "text-foreground",
                )}
              >
                {opt.label}
              </span>
              <span className="block text-xs text-muted-foreground">
                {opt.desc}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** 5칸 막대 스파크라인 — 분포 모양을 직관적으로 보여주는 장식(amber 톤). 의미 정보는 라벨/설명이 전달. */
function ReplanSparkline({
  bars,
  active,
}: {
  bars: readonly number[];
  active: boolean;
}) {
  return (
    <span
      aria-hidden
      className="flex h-7 w-12 shrink-0 items-end justify-between gap-0.5"
    >
      {bars.map((h, i) => (
        <span
          key={i}
          className={cn(
            "w-1 rounded-sm",
            active
              ? "bg-amber-500 dark:bg-amber-400"
              : "bg-amber-300 dark:bg-amber-700",
          )}
          // 높이 1~5 → 20%~100%. 인라인 style 은 동적 비율이라 토큰/페어 무관(색은 className 으로 dark 대응).
          style={{ height: `${(h / 5) * 100}%` }}
        />
      ))}
    </span>
  );
}

// ── 헤더 ────────────────────────────────────────────────────────────────────

function ScheduleHeader({
  view,
  setView,
  filter,
  setFilter,
  subscribedIds,
  onPrev,
  onNext,
  onToday,
  rangeLabel,
  subLabel,
  showFilter,
  showToday,
}: {
  view: ViewMode;
  setView: (v: ViewMode) => void;
  filter: PlanFilter;
  setFilter: (f: PlanFilter) => void;
  subscribedIds: StudyPlanId[];
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  rangeLabel: string;
  subLabel: string | null;
  showFilter: boolean;
  showToday: boolean;
}) {
  return (
    <div className="space-y-3">
      {/* 1행: 뷰 토글 · 오늘 · 범례 */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* 뷰 토글 — tabpanel 연결이 없는 단순 전환이라 role=tab 대신 aria-pressed 버튼 그룹
            (PlanFilterToggle·route-layout 의 토글 패턴과 동일, 스크린리더 누름 상태 명확). */}
        <div
          className="inline-flex rounded-lg border border-border p-0.5"
          role="group"
          aria-label="캘린더 보기 전환"
        >
          {(["week", "month"] as const).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              onClick={() => setView(v)}
              className={cn(
                "rounded-md px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
                view === v
                  ? "bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {v === "week" ? "5일" : "월"}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Legend subscribedIds={subscribedIds} />
          {showToday && (
            <Button type="button" variant="outline" size="sm" onClick={onToday}>
              오늘
            </Button>
          )}
        </div>
      </div>

      {/* 2행: 좌우 네비 + 현재 범위 라벨 (+ 계획 필터) */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrev}
            aria-label={view === "week" ? "이전 날" : "이전 달"}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          <div className="min-w-0 px-1">
            <p className="text-base font-bold text-foreground tabular-nums">
              {rangeLabel}
            </p>
            {subLabel && (
              <p className="text-[11px] text-muted-foreground">{subLabel}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onNext}
            aria-label={view === "week" ? "다음 날" : "다음 달"}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {showFilter && (
          <PlanFilterToggle
            filter={filter}
            setFilter={setFilter}
            subscribedIds={subscribedIds}
          />
        )}
      </div>
    </div>
  );
}

function Legend({ subscribedIds }: { subscribedIds: StudyPlanId[] }) {
  return (
    <div className="flex items-center gap-2.5 text-[11px] text-muted-foreground">
      {subscribedIds.map((id) => (
        <span key={id} className="flex items-center gap-1">
          <span
            className={cn("h-2 w-2 rounded-full", PLAN_LEGEND_DOT[id])}
            aria-hidden
          />
          {id === "theory-4week" ? "이론" : "코딩"}
        </span>
      ))}
    </div>
  );
}

function PlanFilterToggle({
  filter,
  setFilter,
  subscribedIds,
}: {
  filter: PlanFilter;
  setFilter: (f: PlanFilter) => void;
  subscribedIds: StudyPlanId[];
}) {
  const options: { value: PlanFilter; label: string }[] = [
    { value: "all", label: "전체" },
    ...subscribedIds.map((id) => ({
      value: id as PlanFilter,
      label: id === "theory-4week" ? "이론만" : "코딩만",
    })),
  ];
  return (
    <div
      className="inline-flex rounded-lg border border-border p-0.5"
      role="group"
      aria-label="계획 필터"
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={filter === opt.value}
          onClick={() => setFilter(opt.value)}
          className={cn(
            "rounded-md px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
            filter === opt.value
              ? "bg-accent text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── 보조 훅·헬퍼 ──────────────────────────────────────────────────────────────

/** KST 오늘(렌더마다 동일 값이라 메모 불필요하지만 명시적 1회 계산). */
function useToday(): string {
  const [today] = useState(() => todayKstISO());
  return today;
}

/** anchor ISO 에 months 를 더한 그 달 1일 ISO. */
function addMonthsAnchor(iso: string, months: number): string {
  const d = dateFromISO(iso);
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}

/**
 * 5일 윈도우 보조 라벨: "이론 4주 완성 · Day 10~14 · 마무리" 류(첫 구독 계획 기준).
 * 페이지 균등 분배라 "날짜=Day N"이 1:1이 아니므로, 그 창에 실제로 보이는 task 들의 커리큘럼
 * Day(originDayNumber) 범위로 라벨을 만든다. 창에 이 계획 일정이 없으면 null.
 */
function useWeekDayRange(
  data: ScheduleData,
  windowStartISO: string,
): string | null {
  return useMemo(() => {
    const first = data.subscribed[0];
    if (!first) return null;
    const { plan, planId } = first;
    const windowEndISO = addDaysISO(windowStartISO, WINDOW_DAYS - 1);
    let lo = Infinity;
    let hi = -Infinity;
    for (const t of data.allTasks) {
      if (
        t.planId !== planId ||
        t.skipped ||
        t.effectiveDateISO < windowStartISO ||
        t.effectiveDateISO > windowEndISO
      )
        continue;
      if (t.originDayNumber < lo) lo = t.originDayNumber;
      if (t.originDayNumber > hi) hi = t.originDayNumber;
    }
    if (lo === Infinity) return null; // 이 창에 이 계획 일정 없음
    const phase = plan.days[lo - 1]?.phaseLabel ?? "";
    const dayLabel = lo === hi ? `Day ${lo}` : `Day ${lo}~${hi}`;
    return phase
      ? `${STUDY_PLAN_LABELS[planId]} · ${dayLabel} · ${phase}`
      : `${STUDY_PLAN_LABELS[planId]} · ${dayLabel}`;
  }, [data.subscribed, data.allTasks, windowStartISO]);
}
